import { alpha, cardCls, ProgressRing, ProgressBar, fmt } from "./homeUi";
import { levelProgress } from "./HomeHero";

/*
 * The gamification card on Home.
 *
 *   - Steps left  → "Every member starts as a <first level>": the getting-started
 *                   checklist, each step worth the GP the creator configured.
 *   - All done    → the same card becomes "Ways to earn": the creator's point
 *                   values, so there is always a next thing to do.
 *   - Not set up  → a designed empty state. No levels and no point values means
 *                   nothing can be earned, and a progress bar that never moves
 *                   is worse than saying so.
 */

const SPROUT = "M12 21v-8m0 0c0-4 3-7 8-7 0 5-3 7-8 7Zm0 0C12 9 9 6 4 6c0 5 3 7 8 7";

// Point values worth listing under "Ways to earn", in the order a member meets them.
const EARN_ROWS = [
  ["post", "Share a post", "community"],
  ["comment", "Reply to someone", "community"],
  ["like", "Like a post or comment", "community"],
  ["lesson_complete", "Complete a lesson", "course"],
  ["lesson_discussion", "Join a lesson discussion", "course"],
  ["profile_picture", "Add a profile photo", null],
  ["viral_share", "Invite a friend who joins", null],
];

/**
 * "Every member starts as a Seed" — or "at Level 1" when the creator kept the
 * numbered names, since "as a Level 1" doesn't read.
 */
function startsAs(name) {
  const n = String(name || "").trim();
  if (!n) return "Every member starts somewhere";
  if (/^level\b/i.test(n)) return `Every member starts at ${n}`;
  return `Every member starts as ${/^[aeiou]/i.test(n) ? "an" : "a"} ${n}`;
}

function Check({ done, primary }) {
  return done ? (
    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-emerald-500 text-white shadow-sm shadow-emerald-500/30">
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.2"><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
    </span>
  ) : (
    <span className="h-6 w-6 shrink-0 rounded-full border-2 border-gray-300 transition-colors group-hover:border-[color:var(--p)]" style={{ "--p": primary }} />
  );
}

/**
 * Nothing to earn yet — the creator hasn't set up points or levels.
 *
 * `compact` is the rail rendering: the card sits in a column about a third of
 * the page wide, where the side-by-side layout would squeeze the artwork and the
 * copy into two thin strips. Note it can't be a `sm:` breakpoint — those track
 * the viewport, and the viewport is wide exactly when the rail is narrow.
 */
export function GamificationEmpty({ primary, compact }) {
  return (
    <section className={`${cardCls} relative flex h-full items-center overflow-hidden ${compact ? "p-5" : "p-6 sm:p-7"}`} aria-label="Points and levels">
      <div aria-hidden="true" className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full blur-3xl" style={{ background: alpha(primary, 0.14) }} />
      <div className={`relative flex w-full flex-col items-start gap-5 ${compact ? "" : "sm:flex-row sm:items-center"}`}>
        {/* A small ladder of locked levels — the thing that's coming. */}
        <div className="flex h-24 w-28 shrink-0 items-end justify-center gap-1.5 rounded-2xl border border-gray-200 bg-gray-50/80 p-3" aria-hidden="true">
          {[34, 52, 70, 88].map((h, i) => (
            <span
              key={h}
              className="w-4 rounded-md"
              style={{ height: `${h}%`, background: i === 3 ? `linear-gradient(180deg, ${primary}, ${alpha(primary, 0.55)})` : alpha(primary, 0.12 + i * 0.08) }}
            />
          ))}
        </div>
        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em]" style={{ backgroundColor: alpha(primary, 0.1), color: primary }}>
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" /></svg>
            Coming soon
          </span>
          <h3 className="mt-2 text-[17px] font-bold tracking-tight text-gray-900">Points and levels are on their way</h3>
          <p className="mt-1 max-w-md text-[13.5px] leading-relaxed text-gray-500">
            This community hasn’t switched on points and levels yet. Keep learning and joining in — once they’re live, your progress will show up right here.
          </p>
          <div className="mt-3.5 flex flex-wrap gap-2">
            {["Earn points", "Level up", "Unlock rewards"].map((t) => (
              <span key={t} className="rounded-full border border-dashed border-gray-300 px-3 py-1 text-[12px] font-semibold text-gray-500">{t}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomeProgress({ me, gamification, checklist, primary, menuOn, onAction, compact }) {
  if (!gamification?.enabled) return <GamificationEmpty primary={primary} compact={compact} />;

  const steps = Array.isArray(checklist) ? checklist : [];
  const doneCount = steps.filter((s) => s.done).length;
  const remaining = steps.filter((s) => !s.done);
  const gpLeft = remaining.reduce((n, s) => n + Number(s.points || 0), 0);
  const lp = levelProgress(me);
  const firstLevel = me?.levels?.[0]?.name;
  const allDone = !steps.length || !remaining.length;
  const ps = gamification.points_settings || {};
  const earnRows = EARN_ROWS.filter(([k, , menu]) => Number(ps[k] || 0) > 0 && (!menu || menuOn(menu)));
  const pct = steps.length ? Math.round((doneCount / steps.length) * 100) : 100;

  return (
    <section className={`${cardCls} flex h-full flex-col p-5 sm:p-6`} aria-label="Getting started">
      <div className="flex items-center gap-3.5">
        {allDone ? (
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl" style={{ backgroundColor: alpha(primary, 0.12), color: primary }}>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>
          </span>
        ) : (
          <ProgressRing pct={pct} size={48} stroke={5} primary={primary}>
            <span className="text-[11.5px] font-extrabold tabular-nums text-gray-900">{doneCount}/{steps.length}</span>
          </ProgressRing>
        )}
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 text-[15.5px] font-bold tracking-tight text-gray-900">
            {!allDone ? <svg className="h-4 w-4 shrink-0 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d={SPROUT} /></svg> : null}
            <span className="truncate">
              {allDone ? "Ways to earn GP" : startsAs(firstLevel)}
            </span>
          </h3>
          <p className="mt-0.5 text-[13px] text-gray-500">
            {allDone
              ? "You’ve finished getting started. Here’s how to keep climbing."
              : gpLeft > 0
                ? `Finish ${remaining.length} ${remaining.length === 1 ? "step" : "steps"} to earn ${fmt(gpLeft)} GP${lp && !lp.isMax && lp.nextName ? ` on your way to ${lp.nextName}` : ""}.`
                : `Finish ${remaining.length} ${remaining.length === 1 ? "step" : "steps"} to get set up.`}
          </p>
        </div>
      </div>

      {!allDone ? (
        <ul className="mt-4 space-y-2">
          {steps.map((s) => (
            <li key={s.key}>
              <button
                type="button"
                disabled={s.done}
                onClick={() => onAction(s.action)}
                className={`group flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-all ${
                  s.done ? "cursor-default border-gray-200 bg-gray-50/60" : "border-gray-200 bg-surface hover:-translate-y-px hover:border-[color:var(--p)] hover:shadow-sm"
                }`}
                style={{ "--p": alpha(primary, 0.45) }}
              >
                <Check done={s.done} primary={primary} />
                <span className={`min-w-0 flex-1 text-[13.5px] font-semibold leading-snug ${s.done ? "text-gray-400 line-through decoration-gray-300" : "text-gray-800"}`}>
                  {s.title}
                </span>
                {!s.done ? (
                  <span className="hidden text-[12px] font-semibold opacity-0 transition-opacity group-hover:opacity-100 sm:inline" style={{ color: primary }}>
                    Start →
                  </span>
                ) : null}
                {Number(s.points) > 0 ? (
                  <span
                    className={`shrink-0 rounded-full px-2 py-0.5 text-[11.5px] font-bold tabular-nums ${s.done ? "text-emerald-600 dark:text-emerald-300" : ""}`}
                    style={s.done ? undefined : { backgroundColor: alpha(primary, 0.12), color: primary }}
                  >
                    +{fmt(s.points)} GP
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* While steps remain, the creator's other point values ride underneath
          as chips — the checklist is where to start, not all there is. */}
      {!allDone && earnRows.length ? (
        <div className="mt-4">
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400">More ways to earn</p>
          <div className="flex flex-wrap gap-1.5">
            {earnRows.map(([k, label]) => (
              <span key={k} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50/70 py-1 pl-2.5 pr-1 text-[12px] font-medium text-gray-600">
                {label}
                <span className="rounded-full px-1.5 py-px text-[11px] font-bold tabular-nums" style={{ backgroundColor: alpha(primary, 0.12), color: primary }}>+{fmt(ps[k])}</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {allDone && earnRows.length ? (
        <ul className={`mt-4 grid gap-2 ${compact ? "" : "sm:grid-cols-2"}`}>
          {earnRows.map(([k, label]) => (
            <li key={k} className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50/60 px-3.5 py-2.5">
              <span className="truncate text-[13px] font-medium text-gray-700">{label}</span>
              <span className="shrink-0 rounded-full px-2 py-0.5 text-[11.5px] font-bold tabular-nums" style={{ backgroundColor: alpha(primary, 0.12), color: primary }}>
                +{fmt(ps[k])} GP
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {lp && !lp.isMax ? (
        <div className="mt-auto pt-5">
          <div className="mb-2 flex items-center justify-between text-[12px] font-medium tabular-nums text-gray-500">
            <span><b className="font-semibold text-gray-800">{fmt(me?.points)} GP</b> earned</span>
            <span>{lp.nextName} at {fmt(lp.ceil)} GP</span>
          </div>
          <ProgressBar pct={lp.pct} primary={primary} />
        </div>
      ) : null}
    </section>
  );
}
